import type { BlogArticle, PageContent, Product, SiteSettings, SeoPage, Order, Client, SmartBillSettings, SmartBillInvoice, NetopiaSettings, NetopiaPaymentLog } from '../types';

// ────────────────────────────────────────────────────────────────────────────
// Token management — stocat in localStorage, trimis ca header X-CMS-Token
// Rezolva problema: sesiunile PHP nu functioneaza cross-domain (CDN ≠ server PHP)
// ────────────────────────────────────────────────────────────────────────────
const LS_TOKEN = 'cms_api_token';
const LS_USER  = 'cms_user_info';

export function getApiToken(): string { return localStorage.getItem(LS_TOKEN) || ''; }
export function setApiToken(token: string, user: string, role: string) {
  localStorage.setItem(LS_TOKEN, token);
  localStorage.setItem(LS_USER, JSON.stringify({ user, role }));
  localStorage.setItem('cms_auth', '1');
}
export function clearApiToken() {
  localStorage.removeItem(LS_TOKEN);
  localStorage.removeItem(LS_USER);
  localStorage.removeItem('cms_auth');
}
export function getUserInfo(): { user: string; role: string } | null {
  try { return JSON.parse(localStorage.getItem(LS_USER) || 'null'); } catch { return null; }
}

/* ── Baza URL pentru API ────────────────────────────────────────────────── */
// Pe CDN (skywork.website) → nu exista PHP → toate requesturile vor esua si
// se va folosi fallback localStorage. Pe serverul live → /cms-api/ = PHP real.
// Daca CMS-ul este pe un alt domeniu, setati API_BASE cu URL-ul complet:
// ex: 'https://interpersonal.ro/cms-api'
const API_BASE = (import.meta.env.VITE_CMS_API_BASE as string) || '/cms-api';

/* ── Auth ─────────────────────────────────────────────────────────────────── */
export const auth = {
  login: async (user: string, pass: string): Promise<{ ok: boolean; role?: string }> => {
    try {
      const res = await fetch(`${API_BASE}/auth.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'login', username: user, password: pass }),
      });
      const data = await res.json();
      if (data.ok && data.token) {
        setApiToken(data.token, data.user ?? user, data.role ?? 'management');
        return { ok: true, role: data.role ?? 'management' };
      }
      // PHP raspuns negativ (credentiale gresite)
      return { ok: false };
    } catch {
      // PHP indisponibil (CDN / offline) — fallback hardcodat pentru demo
      const fallbacks: Record<string, string> = {
        'admin': '1234qwer', 'marketing': 'marketing2026', 'vanzari': 'vanzari2026',
      };
      const roleMap: Record<string, string> = {
        'admin': 'management', 'marketing': 'marketing', 'vanzari': 'vanzari',
      };
      if (fallbacks[user] === pass) {
        setApiToken('offline-' + user, user, roleMap[user] ?? 'management');
        return { ok: true, role: roleMap[user] ?? 'management' };
      }
      return { ok: false };
    }
  },

  logout: async () => {
    const token = getApiToken();
    if (token && !token.startsWith('offline-')) {
      try {
        await fetch(`${API_BASE}/auth.php`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-CMS-Token': token },
          body: JSON.stringify({ action: 'logout' }),
        });
      } catch { /* ignore */ }
    }
    clearApiToken();
  },

  isLoggedIn: (): boolean => localStorage.getItem('cms_auth') === '1' && !!getApiToken(),
};

/* ── Request helper cu X-CMS-Token ─────────────────────────────────────── */
async function api<T>(endpoint: string, method = 'GET', body?: unknown): Promise<T> {
  const token = getApiToken();
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers['X-CMS-Token'] = token;

  const res = await fetch(`${API_BASE}/${endpoint}`, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });

  if (res.status === 401) {
    // Token expirat — curata si redirect la login
    clearApiToken();
    window.location.hash = '#/login';
    throw new Error('Sesiune expirata. Autentifica-te din nou.');
  }

  if (!res.ok) throw new Error(`API error ${res.status}`);
  return res.json();
}

/* ── Upload cu X-CMS-Token ─────────────────────────────────────────────── */
export const uploadImage = async (file: File): Promise<string> => {
  const fd = new FormData();
  fd.append('file', file);
  const token = getApiToken();
  const headers: Record<string, string> = {};
  if (token) headers['X-CMS-Token'] = token;
  const res = await fetch(`${API_BASE}/upload.php`, { method: 'POST', body: fd, headers });
  const data = await res.json();
  return data.url as string;
};

/* ── API endpoints ──────────────────────────────────────────────────────── */
export const blogApi = {
  list: () => api<BlogArticle[]>('blog.php'),
  get: (id: string) => api<BlogArticle>(`blog.php?id=${id}`),
  create: (data: Omit<BlogArticle, 'id' | 'createdAt' | 'updatedAt'>) => api<BlogArticle>('blog.php', 'POST', data),
  update: (id: string, data: Partial<BlogArticle>) => api<BlogArticle>('blog.php', 'PUT', { id, ...data }),
  delete: (id: string) => api<{ ok: boolean }>('blog.php', 'DELETE', { id }),
};

export const pagesApi = {
  list: () => api<PageContent[]>('pages.php'),
  get: (id: string) => api<PageContent>(`pages.php?id=${id}`),
  update: (id: string, data: Partial<PageContent>) => api<PageContent>('pages.php', 'PUT', { id, ...data }),
};

export const productsApi = {
  list: () => api<Product[]>('products.php'),
  get: (id: string) => api<Product>(`products.php?id=${id}`),
  create: (data: Omit<Product, 'id' | 'updatedAt'>) => api<Product>('products.php', 'POST', data),
  update: (id: string, data: Partial<Product>) => api<Product>('products.php', 'PUT', { id, ...data }),
  delete: (id: string) => api<{ ok: boolean }>('products.php', 'DELETE', { id }),
};

export const settingsApi = {
  get: () => api<SiteSettings>('settings.php'),
  update: (data: Partial<SiteSettings>) => api<SiteSettings>('settings.php', 'PUT', data),
};

export const seoApi = {
  list: () => api<SeoPage[]>('seo.php'),
  update: (id: string, data: Partial<SeoPage>) => api<SeoPage>('seo.php', 'PUT', { id, ...data }),
};

export const ordersApi = {
  list: () => api<Order[]>('orders.php'),
  get: (id: string) => api<Order>(`orders.php?id=${id}`),
  create: (data: Omit<Order, 'id' | 'orderNumber' | 'createdAt' | 'updatedAt'>) => api<Order>('orders.php', 'POST', data),
  update: (id: string, data: Partial<Order>) => api<Order>('orders.php', 'PUT', { id, ...data }),
  delete: (id: string) => api<{ ok: boolean }>('orders.php', 'DELETE', { id }),
};

export const clientsApi = {
  list: () => api<Client[]>('clients.php'),
  get: (id: string) => api<Client>(`clients.php?id=${id}`),
  create: (data: Omit<Client, 'id' | 'createdAt' | 'updatedAt'>) => api<Client>('clients.php', 'POST', data),
  update: (id: string, data: Partial<Client>) => api<Client>('clients.php', 'PUT', { id, ...data }),
  delete: (id: string) => api<{ ok: boolean }>('clients.php', 'DELETE', { id }),
};

// ─── SmartBill API ────────────────────────────────────────────────────────────
const DEFAULT_SB_SETTINGS: SmartBillSettings = {
  username: '', token: '', companyVatCode: 'RO34799221', seriesName: 'FACT',
  currency: 'RON', taxName: 'TVA', taxPercentage: 19, issuerName: 'INTERPERSONAL S.R.L.',
  sendEmailOnCreate: true, isDraft: false,
};

const DEFAULT_NETOPIA_SETTINGS: NetopiaSettings = {
  posSignature: '', apiKey: '', currency: 'RON', language: 'ro', isLive: false,
  notifyUrl: 'https://interpersonal.ro/cms-api/netopia-ipn.php',
  redirectUrl: 'https://interpersonal.ro/multumim',
};

const LS_SB = 'cms_smartbill_settings';
const LS_NETOPIA = 'cms_netopia_settings';
const LS_INVOICES = 'cms_invoices';
const LS_PAYMENTS = 'cms_payment_logs';

export const smartbillApi = {
  getSettings: (): SmartBillSettings => {
    try { return JSON.parse(localStorage.getItem(LS_SB) || '{}') || DEFAULT_SB_SETTINGS; } catch { return DEFAULT_SB_SETTINGS; }
  },
  saveSettings: (s: SmartBillSettings): void => localStorage.setItem(LS_SB, JSON.stringify(s)),

  listInvoices: (): SmartBillInvoice[] => {
    try { return JSON.parse(localStorage.getItem(LS_INVOICES) || '[]'); } catch { return []; }
  },
  saveInvoices: (inv: SmartBillInvoice[]): void => localStorage.setItem(LS_INVOICES, JSON.stringify(inv)),

  createInvoice: async (invoice: Omit<SmartBillInvoice, 'id' | 'createdAt'>): Promise<SmartBillInvoice> => {
    try {
      const res = await api<SmartBillInvoice>('smartbill.php', 'POST', { action: 'createInvoice', ...invoice });
      const all = smartbillApi.listInvoices();
      all.unshift(res);
      smartbillApi.saveInvoices(all);
      return res;
    } catch {
      const local: SmartBillInvoice = { ...invoice, id: `inv-${Date.now()}`, status: 'draft', createdAt: new Date().toISOString().split('T')[0] };
      const all = smartbillApi.listInvoices();
      all.unshift(local);
      smartbillApi.saveInvoices(all);
      return local;
    }
  },

  sendInvoiceEmail: async (invoiceId: string): Promise<boolean> => {
    try { await api('smartbill.php', 'POST', { action: 'sendEmail', invoiceId }); return true; } catch { return false; }
  },

  testConnection: async (): Promise<{ ok: boolean; message: string }> => {
    try { return await api<{ ok: boolean; message: string }>('smartbill.php', 'POST', { action: 'testConnection' }); }
    catch (e) { return { ok: false, message: String(e) }; }
  },
};

// ─── Netopia API ──────────────────────────────────────────────────────────────
export const netopiaApi = {
  getSettings: (): NetopiaSettings => {
    try { return { ...DEFAULT_NETOPIA_SETTINGS, ...JSON.parse(localStorage.getItem(LS_NETOPIA) || '{}') }; } catch { return DEFAULT_NETOPIA_SETTINGS; }
  },
  saveSettings: (s: NetopiaSettings): void => localStorage.setItem(LS_NETOPIA, JSON.stringify(s)),

  listPayments: (): NetopiaPaymentLog[] => {
    try { return JSON.parse(localStorage.getItem(LS_PAYMENTS) || '[]'); } catch { return []; }
  },

  initiatePayment: async (orderId: string, amount: number, customerEmail: string, customerName: string, description: string): Promise<{ redirectUrl: string; token?: string }> => {
    return api<{ redirectUrl: string; token?: string }>('netopia.php', 'POST', { action: 'initiate', orderId, amount, customerEmail, customerName, description });
  },

  testConnection: async (): Promise<{ ok: boolean; message: string }> => {
    try { return await api<{ ok: boolean; message: string }>('netopia.php', 'POST', { action: 'testConnection' }); }
    catch (e) { return { ok: false, message: String(e) }; }
  },
};
